/* Types shared by the API server and the web client. */

/**
 * The dashboard tracks one Masterfile per operating country. Every snapshot
 * belongs to exactly one of them, so the two never mix in a statistic.
 */
export type Country = "ID" | "MY";

export const COUNTRIES: { key: Country; label: string; file: string }[] = [
  { key: "ID", label: "Indonesia", file: "ID Master File 27-28.xlsx" },
  { key: "MY", label: "Malaysia", file: "MY Master File 27-28.xlsx" },
];

export function isCountry(v: unknown): v is Country {
  return v === "ID" || v === "MY";
}

export type CompKey =
  | "accommodation" | "cat_e" | "activity"
  | "entrance_fee" | "transport" | "guide" | "train";

export type Group = "core" | "core_alt" | "additional";
export type Severity = "high" | "medium" | "low";

/** One supplier/product row from one Masterfile sheet. */
export interface Rec {
  uid: string;
  comp: CompKey;
  compLabel: string;
  group: Group;
  sheet: string;
  excelRow: number;
  code: string | null;
  name: string | null;
  product: string | null;
  town: string | null;
  region: string | null;
  cat: string | null;

  recvRaw: string | null;
  received: boolean;
  recvDate: string | null;
  reqDate: string | null;

  nrRaw: string | null;
  nrDone: boolean;
  nrDate: string | null;
  nrPic: string | null;

  estRaw: string | null;
  estDone: boolean;
  estDate: string | null;
  estPic: string | null;

  rateType: string | null;
  selling: string | null;
  ccBy: string | null;
  ccDate: string | null;
  upDate: string | null;
  exDate: string | null;
  cancel: string | null;
}

export interface Finding {
  severity: Severity;
  rule: string;
  comp: CompKey;
  compLabel: string;
  sheet: string;
  excelRow: number;
  code: string | null;
  name: string | null;
  product: string | null;
  cat: string | null;
  region: string | null;
  message: string;
  uid: string;
}

export interface ComponentInfo {
  key: CompKey;
  label: string;
  group: Group;
  sheet: string;
  rows: number;
}

export interface Mirror {
  primarySheet: string;
  mirrorSheet: string;
  label: string;
  primaryRows: number;
  mirrorRows: number;
  primaryDone: number;
  mirrorDone: number;
  onlyInMirror: string[];
  onlyInPrimary: string[];
  identical: boolean;
}

/** Snapshot header - one parsed Masterfile upload. */
export interface SnapshotMeta {
  id: number;
  country: Country;
  label: string | null;
  sourceFile: string;
  generatedAt: string;
  recordCount: number;
  findingCount: number;
  createdAt: string;
  isSeed: boolean;
}

/** Everything the dashboard needs for one snapshot. */
export interface SnapshotPayload {
  meta: SnapshotMeta;
  sheetsInWorkbook: string[];
  components: ComponentInfo[];
  mirrors: Mirror[];
  notes: string[];
  records: Rec[];
  findings: Finding[];
  definitions: Record<string, string>;
}

/** Aggregated counts over a set of records. Buckets x* are mutually exclusive. */
export interface Stats {
  total: number;
  received: number;
  notReceived: number;
  priced: number;
  estimated: number;
  awaiting: number;
  estOutstanding: number;
  entered: number;
  both: number;
  xPriced: number;
  xEstOnly: number;
  xAwaiting: number;
  xNone: number;
  crosschecked: number;
  uploaded: number;
  exported: number;
  completion: number;
}

export interface TrendPoint {
  snapshotId: number;
  label: string | null;
  generatedAt: string;
  sourceFile: string;
  stats: Stats;
}

export interface PicRow {
  pic: string;
  newRate: number;
  estimation: number;
  entered: number;
  crosschecked: number;
  firstEntry: string | null;
  lastEntry: string | null;
  activeDays: number;
  perDay: number;
}

/* ------------------------------------------------- pace & projection */

export interface MonthCount {
  month: string;   // YYYY-MM
  count: number;
}

/** Throughput measured from input dates, plus what it implies for the finish. */
export interface Pace {
  byMonth: MonthCount[];
  activeMonths: number;
  totalDated: number;
  /** Mean entries per month over the recent active months (sample size below). */
  recentPerMonth: number;
  recentMonths: number;
  remaining: number;
  /** Null when nothing has been entered yet, so no pace can be derived. */
  monthsToFinish: number | null;
  projectedFinish: string | null;   // YYYY-MM-DD
  /** Only set once a target date exists. */
  requiredPerMonth: number | null;
  monthsToTarget: number | null;
  onTrack: boolean | null;
}

/* ------------------------------------------------------------ worklist */

export type WorkKind = "price" | "reprice" | "crosscheck" | "estimate" | "export";

/** One outstanding action, ready to hand to a person. */
export interface WorkItem {
  uid: string;
  kind: WorkKind;
  priority: number;          // 1 = most urgent
  action: string;
  why: string;
  code: string | null;
  name: string | null;
  compLabel: string;
  sheet: string;
  excelRow: number;
  region: string | null;
  town: string | null;
  cat: string | null;
  owner: string | null;      // whoever already touched it, if anyone
  waitingSince: string | null;
  ageDays: number | null;
}

/* ------------------------------------------------------------ settings */

export interface Settings {
  /** Season deadline, YYYY-MM-DD. Null until someone sets it. Per country,
      because the Increment sheet gives ID and MY different cut-off dates. */
  targetDate: string | null;
}

/* ------------------------------------------------------- sharepoint sync */

/** What the server can tell the client about the SharePoint connection. */
export interface SyncStatus {
  country: Country;
  /** False when the server has no Graph credentials configured. */
  configured: boolean;
  fileName: string | null;
  /** Last time a sync actually ran, successful or not. */
  lastCheckedAt: string | null;
  /** Last time a changed file was ingested into a new snapshot. */
  lastSyncedAt: string | null;
  lastSnapshotId: number | null;
  /** Remote file timestamp at the last successful download. */
  remoteModifiedAt: string | null;
  error: string | null;
  /** Poll interval in seconds; 0 when polling is off. */
  pollSeconds: number;
}
